import { useState } from "react";
import { Link } from "react-router";
import { Chip } from "../ui";
import { AndamentoDosModulos } from "./AndamentoDosModulos";
import { textoDaEtapaDoCliente } from "../../nucleo/textosDaEsteira";
import { classes } from "../../nucleo/classes";
import {
  AVISO_SEM_PERMISSAO_NA_ETAPA,
  AVISO_SEM_PERMISSAO_NA_ETAPA_INTERNO,
  AVISO_SEM_TELA,
  CHIP_DO_ESTADO,
  ETAPAS_COM_TELA,
  rotuloDaVez,
  type VozDaEsteira,
  ROTULO_DO_ESTADO,
  destinoDaEtapa,
  destinoInternoDaEtapa,
  PERMISSAO_DA_REVISAO_INTERNA,
  formatarData,
  permissaoDaEtapa,
  textoDaEsteira,
  type Esteira,
  type EtapaDaEsteira,
} from "../../nucleo/implantacoes";
import { podeNaSessao, useSessao } from "../../nucleo/sessao";
import { ATALHO_DA_ETAPA, type SecaoDaImplantacao } from "../../nucleo/secoesDaImplantacao";

/**
 * As cinco etapas, com estado, de quem é a vez e o que vem depois.
 *
 * Nada aqui é calculado no navegador: estado, vez e resumo chegam derivados da linha do tempo,
 * no servidor. É o que garante que esta tela e o console interno não contem histórias
 * diferentes sobre a mesma implantação — as duas leem a mesma derivação.
 *
 * Com `aoAbrirSecao` (o console, SUS-15400), a esteira fica na horizontal, como um percurso até a
 * virada, e o botão de cada etapa abre o card da seção na mesma tela em vez de levar a outra.
 */
export function EsteiraDaImplantacao({
  esteira,
  voz = "cliente",
  aoAbrirSecao,
}: {
  esteira: Esteira;
  voz?: VozDaEsteira;
  aoAbrirSecao?: (secao: SecaoDaImplantacao) => void;
}) {
  const horizontal = aoAbrirSecao !== undefined;
  const virada = horizontal ? formatarData(esteira.prazo.entrega_prevista) : null;
  const lista = (
    <ol className={classes("esteira", horizontal && "esteira--horizontal")} data-testid="esteira">
      {esteira.etapas.map((etapa) => (
        <EtapaDaEsteiraItem
          key={etapa.etapa}
          etapa={etapa}
          implantacaoId={esteira.implantacao_id}
          voz={voz}
          atual={etapa.etapa === esteira.etapa_atual}
          clienteConcluiu={
            (etapa.etapa === "contrato" || etapa.etapa === "infraestrutura" || etapa.etapa === "kickoff") &&
            (etapa.estado === "concluida" || (etapa.etapa === "infraestrutura" && etapa.estado === "em_andamento" && etapa.vez === "geovendas"))
          }
          esteira={esteira}
          {...(aoAbrirSecao ? { aoAbrirSecao } : {})}
        />
      ))}
      {horizontal ? (
        <li className="esteira__virada" data-testid="virada-prevista">
          <span className="esteira__bandeira" aria-hidden="true">⚑</span>
          <span>
            Virada prevista
            <br />
            <b className="numero-tabular">{virada ?? "a definir"}</b>
          </span>
        </li>
      ) : null}
    </ol>
  );
  if (!horizontal) return lista;
  /* Em tela estreita o percurso rola dentro do próprio bloco; focável, para o teclado também
     alcançar as etapas da direita. */
  return (
    <div className="esteira__trilho" role="region" aria-label="Etapas da implantação" tabIndex={0}>
      {lista}
    </div>
  );
}

function EtapaDaEsteiraItem({
  etapa,
  implantacaoId,
  atual,
  voz,
  clienteConcluiu,
  esteira,
  aoAbrirSecao,
}: {
  etapa: EtapaDaEsteira;
  implantacaoId: string;
  atual: boolean;
  voz: VozDaEsteira;
  clienteConcluiu: boolean;
  esteira: Esteira;
  aoAbrirSecao?: (secao: SecaoDaImplantacao) => void;
}) {
  const [aberta, setAberta] = useState(false);
  const sessao = useSessao();
  const quando = formatarData(etapa.ocorrido_em);
  const concluida = etapa.estado === "concluida";
  /* A tela da etapa exige permissão própria, e o card não pode oferecer o que o servidor nega:
     a etapa 2 é governada por `infra:*` e o key user, que tem `kickoff:ver`, não a alcança. */
  const interna = voz === "interna";
  const permissao = interna
    ? etapa.etapa === "reuniao" ? "ata:ver" : PERMISSAO_DA_REVISAO_INTERNA
    : permissaoDaEtapa(etapa.etapa);
  const alcanca = permissao === null || podeNaSessao(sessao.data, permissao);
  /* Só na etapa que está cobrando ação agora. Repetir o aviso em toda a lista viraria
     ruído e enterraria justamente a linha que importa. */
  const cobraSemCaminho =
    atual && etapa.vez === "cliente" && !concluida && !ETAPAS_COM_TELA.has(etapa.etapa);
  /* Etapa bloqueada não ganha caminho: o formulário existe, mas alguma coisa antes dela ainda
     não aconteceu, e convidar a pessoa a entrar seria prometer o que o servidor vai recusar. */
  const destino =
    (etapa.estado === "bloqueada" && etapa.etapa !== "reuniao") || !alcanca
      ? null
      : interna
        ? destinoInternoDaEtapa(etapa.etapa, implantacaoId)
        : destinoDaEtapa(etapa.etapa);
  /* Quem lê pelo console não responde pelo cliente: para a consultora o convite é sempre a
     leitura, nunca "Responder agora" — que prometia um formulário que não é dela. */
  const cobrando = !interna && atual && etapa.vez === "cliente" && !concluida;
  /* A explicação fica atrás do clique, mas o que cobra ação do cliente agora não: o caminho para
     responder, e o aviso de quando não há caminho, aparecem com o card recolhido. */
  const visivel = voz === "interna" || aberta;
  const mostraAcao = visivel || cobrando;
  /* O card da seção existe mesmo com a etapa bloqueada, mas oferecê-lo antes de a etapa abrir
     seria mandar a pessoa a uma seção sem nada daquela etapa ainda. Sem a permissão da etapa, o
     atalho também não aparece: ele ficaria logo abaixo do aviso de que ela não a alcança, e o
     clique só levaria a outra negativa. */
  const atalho =
    aoAbrirSecao && etapa.estado !== "bloqueada" && alcanca ? ATALHO_DA_ETAPA[etapa.etapa] : undefined;

  return (
    <li
      className={classes(
        "esteira__etapa",
        concluida && "esteira__etapa--concluida",
        voz === "cliente" && clienteConcluiu && "esteira__etapa--parte-concluida",
        atual && !concluida && "esteira__etapa--atual",
        etapa.estado === "bloqueada" && "esteira__etapa--bloqueada",
      )}
      data-testid={`etapa-${etapa.etapa}`}
      data-estado={etapa.estado}
      data-vez={etapa.vez}
    >
      {aoAbrirSecao && atual && !concluida ? (
        <span className="esteira__selo-atual" data-testid="etapa-atual">Etapa atual</span>
      ) : null}
      <span className="esteira__marca" aria-hidden="true">
        {concluida ? "✓" : etapa.ordem}
      </span>

      <div className="esteira__corpo">
        <h3 className="esteira__titulo">
          {voz === "cliente" ? (
            <button type="button" className="esteira__alternar" aria-expanded={aberta}
              onClick={() => setAberta((valor) => !valor)}>
              {etapa.ordem}. {etapa.nome} <span aria-hidden="true">{aberta ? "▴" : "▾"}</span>
            </button>
          ) : `${etapa.ordem}. ${etapa.nome}`}
          {/* De quem é a vez fica ao lado do nome: é a primeira pergunta de quem abre a tela. */}
          <span
            className={classes(
              "esteira__vez",
              etapa.vez === "cliente" && "esteira__vez--cliente",
            )}
          >
            {rotuloDaVez(etapa.vez, voz)}
          </span>
        </h3>
        {voz === "cliente" && clienteConcluiu ? <span className="esteira__parte-concluida">Sua parte: concluída</span> : null}
        {visivel ? <>
        {voz === "cliente" ? <div className="esteira__explicacao">
          <b>O que acontece aqui:</b> {textoDaEtapaDoCliente(etapa.etapa).explicacao}
          {textoDaEtapaDoCliente(etapa.etapa).proxima ? <p><b>Próxima etapa:</b> {textoDaEtapaDoCliente(etapa.etapa).proxima}</p> : null}
        </div> : null}
        <p className="esteira__resumo">{textoDaEsteira(etapa.resumo, voz)}</p>
        {quando ? <p className="esteira__quando">Registrado em {quando}</p> : null}
        </> : null}
        {mostraAcao && cobraSemCaminho ? (
          <p className="esteira__sem-tela" data-testid={`sem-tela-${etapa.etapa}`}>
            {AVISO_SEM_TELA}
          </p>
        ) : null}
        {mostraAcao && !alcanca && !concluida && etapa.estado !== "bloqueada" ? (
          <p className="esteira__sem-tela" data-testid={`sem-permissao-${etapa.etapa}`}>
            {interna ? AVISO_SEM_PERMISSAO_NA_ETAPA_INTERNO : AVISO_SEM_PERMISSAO_NA_ETAPA}
          </p>
        ) : null}
        {atalho ? (
          <p className="esteira__acao">
            <button
              type="button"
              className="botao botao--pequeno botao--fantasma"
              data-testid={`abrir-etapa-${etapa.etapa}`}
              onClick={() => aoAbrirSecao?.(atalho.secao)}
            >
              {atalho.rotulo}
            </button>
          </p>
        ) : mostraAcao && destino ? (
          <p className="esteira__acao">
            {/* O card é o caminho: era ele que anunciava "sua vez" e não levava a lugar
                nenhum. Concluída continua clicável — o cliente quer reler o que respondeu. */}
            <Link
              to={destino}
              className={classes(
                "botao botao--pequeno",
                cobrando ? "botao--verde" : "botao--fantasma",
              )}
              data-testid={`abrir-etapa-${etapa.etapa}`}
            >
              {etapa.etapa === "reuniao"
                ? "Ver reunião"
                : concluida || interna
                ? "Ver respostas"
                : cobrando
                  ? "Responder agora"
                  : "Abrir formulário"}
            </Link>
          </p>
        ) : null}
        {aberta && voz === "cliente" && etapa.etapa === "execucao" ? <AndamentoDosModulos esteira={esteira} /> : null}
      </div>

      <div className="esteira__lado">
        {/* O estado também vai como atributo no `li`: cor sozinha não serve para quem não a
            enxerga, nem para o teste automatizado. */}
        <Chip estado={CHIP_DO_ESTADO[etapa.estado]}>{ROTULO_DO_ESTADO[etapa.estado]}</Chip>
      </div>
    </li>
  );
}
